import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  SimpleChanges,
  computed,
  input,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EditorComponent, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { switchMap } from 'rxjs';
import { TinyMceHelper } from '../../../helpers/tinymce.helper';
import { InputContainerModule } from '../input-container/input-container.module';

@UntilDestroy()
@Component({
  selector: 'lib-rich-text-editor',
  standalone: true,
  imports: [InputContainerModule, EditorComponent, ReactiveFormsModule],
  providers: [
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
  ],
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextEditorComponent implements OnChanges {
  label = input<string>('');
  id = input.required<string>();
  maxLength = input<number | undefined>(undefined);
  editorFormGroup = input.required<FormGroup>();
  editorFormControl = input.required<FormControl<string>>();
  defaultErrorText = input<string>('');
  required = input<boolean>(false);
  translationMissingError = input<string>();
  questionMarkText = input<string>('');

  errorText = signal('');
  limitTextCopied = 'Texten var för lång och har inte klistrats in';
  limitText = 'Teckengräns uppnådd';
  lastvalidDescriptionValue!: string;
  // Rått HTML-innehåll – källa för teckenräkningen.
  private readonly content = signal('');
  readonly length = computed(() => this.htmlTextLength(this.content()));
  valueAtMaxLength!: string;
  tinyMceConfig: EditorComponent['init'];

  constructor(
    private tinyMceHelper: TinyMceHelper,
    private cdr: ChangeDetectorRef,
  ) {
    this.tinyMceConfig = this.tinyMceHelper.tinyMceConfig;

    // Räknar tecken via formControl.valueChanges eftersom TinyMCE:s onEditorChange
    // bara triggas vid ångringssteg och därför inte håller räknaren i synk.
    // switchMap säkerställer att ett byte av kontroll avslutar tidigare prenumeration.
    toObservable(this.editorFormControl)
      .pipe(
        switchMap((control) => control.valueChanges),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        this.content.set(value);
        this.enforceCharacterLimit();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.content.set(this.editorFormControl().value);

    if (
      changes['formGroup'] ||
      changes['maxLength'] ||
      changes['editorFormGroup']?.currentValue?.value?.list?.description !==
        undefined
    ) {
      this.enforceCharacterLimit(
        changes['maxLength']?.currentValue !==
          changes['maxLength']?.previousValue ||
          changes['editorFormGroup']?.currentValue?.value?.list?.description !==
            undefined,
      );
    }
    if (changes['defaultErrorText']) {
      this.errorText.set(this.defaultErrorText() ?? '');
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (this.defaultErrorText()) {
      return;
    }

    if (
      event.code === 'Backspace' ||
      event.code === 'Delete' ||
      this.errorText() === this.limitTextCopied
    ) {
      this.errorText.set('');
    }
  }

  onBlur() {
    if (this.defaultErrorText()) {
      this.processExternalLinks();
      return;
    }

    this.errorText.set('');
    this.processExternalLinks();
  }

  private enforceCharacterLimit(truncateExcess = false) {
    const maxLength = this.maxLength();
    // Ingen teckengräns satt – ingen räkning eller trunkering behövs.
    if (!maxLength) {
      return;
    }

    const length = this.length();

    if (length === maxLength) {
      this.valueAtMaxLength = this.content();
    } else if (length === maxLength + 1 && this.valueAtMaxLength) {
      this.errorText.set(this.limitText);
      this.setContent(this.valueAtMaxLength);
    } else if (length > maxLength + 1) {
      if (truncateExcess) {
        this.setContent(this.trimToLimit(this.content(), maxLength));
        const errorText = 'Texten var för lång och har kortats ner';
        this.limitTextCopied = errorText;
        this.errorText.set(errorText);
      } else {
        this.setContent(this.lastvalidDescriptionValue);
        const errorText = `Texten var för lång och har inte klistrats in`;
        this.errorText.set(errorText);
        this.limitTextCopied = errorText;
      }
    }

    if (this.length() <= maxLength) {
      this.lastvalidDescriptionValue = this.content();
    }
    this.cdr.detectChanges();
  }

  private processExternalLinks() {
    const processed = this.tinyMceHelper.processExternalLinks(this.content());

    if (processed !== this.content()) {
      this.setContent(processed);
    }
  }

  // Uppdaterar både räknarkällan och formulärkontrollen utan att trigga en ny räkning.
  private setContent(value: string) {
    this.content.set(value);
    this.editorFormControl().patchValue(value, { emitEvent: false });
  }

  // Kortar ner HTML-strängen 40 tecken i taget tills texten ryms inom gränsen.
  private trimToLimit(html: string, maxLength: number): string {
    let trimmed = html;
    while (this.htmlTextLength(trimmed) > maxLength + 1) {
      trimmed = trimmed.slice(0, -40);
    }
    return trimmed;
  }

  // Räknar antal synliga tecken genom att ta bort HTML-taggar.
  private htmlTextLength(html: string): number {
    const span = document.createElement('span');
    // TinyMCE serialiserar blockelement med radbrytningar/indrag mellan taggarna.
    // Ta bort formateringsmellanrum som ligger enbart mellan taggar så att
    // räkningen matchar editorns egen teckenräknare.
    span.innerHTML = (html ?? '').replaceAll(/>\s+</g, '><');
    return span.textContent?.length ?? 0;
  }
}
